import { Button, Card, Divider, Flex, Typography } from 'antd'
import React, { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { AGENCY, GROWTH, PRO, STARTER, Tier, WEBSITE } from '../tier'
import { formatPrice, TierPricing } from '../pricing'
import { PlanFeature, PlanFeatureRow } from './plan-features'

// A first-person tagline per plan, below its title. Looked up dynamically, so registered for i18next-parser here:
// t('For your website')
// t("I'll get to work for you, 24/7")
// t('Like having a whole team, 24/7')
// t('For professional recruiters, 24/7')
// t('Put me to work across multiple organisations')
export const TIER_TAGLINES: Record<Tier, string> = {
    [WEBSITE]: 'For your website',
    [STARTER]: "I'll get to work for you, 24/7",
    [GROWTH]: 'Like having a whole team, 24/7',
    [PRO]: 'For professional recruiters, 24/7',
    [AGENCY]: 'Put me to work across multiple organisations',
}

/**
 * The price lines of a plan card for a period. Yearly shows the effective monthly cost, rounded to whole euros, with the
 * monthly price struck through and the yearly total below.
 */
export function useTierPrice(): (
    tierPricing: TierPricing,
    period: 'monthly' | 'yearly',
    currency: string
) => Pick<TierCardProps, 'price' | 'regularPrice' | 'priceSuffix' | 'priceNote'> | null {
    const { t } = useTranslation()

    return (tierPricing, period, currency) => {
        const periodPricing = tierPricing[period]

        if (!periodPricing) {
            return null
        }

        if (period === 'monthly') {
            return { price: formatPrice(periodPricing.amount, currency), priceSuffix: t('per month') }
        }

        return {
            price: formatPrice(Math.round(periodPricing.amount / 12 / 100) * 100, currency),
            regularPrice: tierPricing.monthly ? formatPrice(tierPricing.monthly.amount, currency) : undefined,
            priceSuffix: t('per month'),
            priceNote: t('{{price}} per year', { price: formatPrice(periodPricing.amount, currency) }),
        }
    }
}

// Title, description, divider, price, action and divider: the rows above the features.
const HEADER_ROWS = 6

export type TierCardProps = {
    title: string
    description?: string
    price: string
    regularPrice?: string
    priceSuffix?: string
    priceNote?: string
    // Overhangs the top edge, for the most popular or recommended plan.
    badge?: string
    features: PlanFeature[]
    // Feature rows to span, the most any card in the grid has.
    featureRows?: number
    selected?: boolean
    // The plan the company is on: quieter, so the plans above it stand out.
    muted?: boolean
    onClick?: () => void
    action: ReactNode
}

/**
 * A plan side by side with others in a grid. The card and its body are subgrids of that grid, one row per block plus one
 * per feature, so a line that wraps in one card moves that row down in all of them.
 */
export function TierCard({
    title,
    description,
    price,
    regularPrice,
    priceSuffix,
    priceNote,
    badge,
    features,
    featureRows = features.length,
    selected = false,
    muted = false,
    onClick,
    action,
}: TierCardProps) {
    // Each block spaces itself with margins, the grid's gap is only between cards.
    const subgrid: CSSProperties = {
        display: 'grid',
        gridRow: `span ${HEADER_ROWS + featureRows}`,
        gridTemplateRows: 'subgrid',
        rowGap: 0,
    }

    return (
        <Card
            // Selection owns the periwinkle border; the badge owns a soft lift (no border) so the two never compete for
            // the eye. Always border-2 so only the colour changes on select/hover — a width change would nudge the card
            // and reflow its neighbours.
            className={clsx('relative w-full rounded-2xl border-2 transition-colors', {
                'cursor-pointer': onClick,
                'shadow-card': badge,
                'bg-surface-muted': muted,
                'border-primary': selected,
                'border-hairline': !selected,
                'hover:border-primary/30': !selected && onClick,
            })}
            style={subgrid}
            // The body's clearfix pseudo-elements would otherwise take up grid rows.
            classNames={{ body: 'text-left before:hidden after:hidden' }}
            styles={{ body: subgrid }}
            onClick={onClick}
        >
            {/* Overhangs the top edge so it takes no layout height — every card's title stays at the top. */}
            {badge && (
                <span className="absolute -top-3 left-6 z-10 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">
                    {badge}
                </span>
            )}

            <Typography.Title
                level={4}
                className="m-0 font-semibold"
            >
                {title}
            </Typography.Title>
            <div className="mt-1">
                {description && <Typography.Text type="secondary">{description}</Typography.Text>}
            </div>

            <Divider className="my-3" />

            {/* Reserved rows above/below keep prices aligned whether or not a card shows a struck-through regular price
                or a period label. */}
            <div>
                <div className="h-5">
                    {regularPrice && (
                        <Typography.Text
                            delete
                            type="secondary"
                            className="whitespace-nowrap text-base"
                        >
                            {regularPrice}
                        </Typography.Text>
                    )}
                </div>
                <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                    <span className="text-4xl font-bold text-ink">{price}</span>
                    {priceSuffix && <span className="text-sm text-ink-tertiary">{priceSuffix}</span>}
                </div>
                <div className="mt-2 h-5">
                    {priceNote && <Typography.Text type="secondary">{priceNote}</Typography.Text>}
                </div>
            </div>

            <Flex className="mt-4">{action}</Flex>

            <Divider className="my-3" />

            {features.map((feature, index) => (
                <PlanFeatureRow
                    key={index}
                    feature={feature}
                    className="mb-2 self-start"
                />
            ))}
        </Card>
    )
}

/**
 * Agency below the plan cards, as the step up from Pro: what it adds and a way to get in touch, it is never bought here.
 */
export function AgencyTier() {
    const { t } = useTranslation()

    return (
        <Card className="w-full rounded-2xl border-2 border-hairline">
            <Flex
                justify="space-between"
                align="center"
                gap="large"
                wrap
            >
                <Flex
                    vertical
                    gap={4}
                    className="max-w-xl"
                >
                    <Typography.Title
                        level={4}
                        className="m-0 font-semibold"
                    >
                        {t('tier.agency')}
                    </Typography.Title>
                    <Typography.Text type="secondary">{t(TIER_TAGLINES[AGENCY])}</Typography.Text>
                    <Typography.Text>
                        {[t('Multiple clients'), t('Sub accounts'), t('One overview')].join(' · ')}
                    </Typography.Text>
                </Flex>
                <Flex
                    vertical
                    align="end"
                    gap="small"
                >
                    <span className="text-3xl font-bold whitespace-nowrap text-ink">{t('Custom')}</span>
                    <Button
                        href="mailto:hi@evaa.chat?subject=Agency"
                        target="_blank"
                    >
                        {t('Contact')}
                    </Button>
                </Flex>
            </Flex>
        </Card>
    )
}
